import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';

export default function App() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [highlightedIdx, setHighlightedIdx] = useState(-1); // Arrow key navigation
  const [selectedResult, setSelectedResult] = useState(null);
  const [detailStack, setDetailStack] = useState([]); // Stack of opened panels
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [latencies, setLatencies] = useState([]);
  const resultsListRef = useRef(null);
  const activeQueryRef = useRef('');
  const searchCacheRef = useRef(new Map());
  const inFlightRef = useRef(new Map());
  const lookupCacheRef = useRef(new Map());

  // Search as you type
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setHighlightedIdx(-1);
      setLoading(false);
      return;
    }

    const normalizedQuery = query.trim().toLowerCase();
    activeQueryRef.current = normalizedQuery;
    const cached = searchCacheRef.current.get(normalizedQuery);
    if (cached) {
      setResults(cached.results || []);
      setHighlightedIdx(-1);
      setError('');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    const inFlight = inFlightRef.current.get(normalizedQuery);
    const requestStart = performance.now();
    const overallStart = requestStart;

    const fetchResults = async () => {
      try {
        const response = inFlight || axios.get('/api/search', { params: { q: normalizedQuery } });
        if (!inFlight) {
          inFlightRef.current.set(normalizedQuery, response);
        }

        const result = await response;
        const responseQuery = String(result?.data?.query || normalizedQuery).toLowerCase();
        const normalizedResponseQuery = responseQuery.trim().toLowerCase();
        const latestQuery = activeQueryRef.current;

        if (latestQuery.startsWith(normalizedResponseQuery)) {
          const requestEnd = performance.now();
          const networkLatency = Math.round(requestEnd - requestStart);
          const totalLatency = Math.round(requestEnd - overallStart);
          const responseResults = result.data.results || [];

          setResults(responseResults);
          searchCacheRef.current.set(responseQuery, { results: responseResults });
          setHighlightedIdx(-1);
          setError('');
          setLatencies(prev => [...prev.slice(-4), `${networkLatency}/${totalLatency}`]);
        }
      } catch (err) {
        const latestQuery = activeQueryRef.current;
        const errorQuery = String(err.config?.params?.q || normalizedQuery).toLowerCase();
        if (latestQuery === errorQuery) {
          const errorMsg = err.response?.data?.error || err.message || 'Search failed';
          setError(errorMsg);
        }
      } finally {
        if (activeQueryRef.current === normalizedQuery) {
          setLoading(false);
        }
        if (!inFlight) {
          inFlightRef.current.delete(normalizedQuery);
        }
      }
    };

    fetchResults();
  }, [query]);

  // Auto-scroll highlighted item into view
  useEffect(() => {
    if (resultsListRef.current && highlightedIdx >= 0) {
      const items = resultsListRef.current.querySelectorAll('.result-item');
      if (items[highlightedIdx]) {
        items[highlightedIdx].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIdx]);

  // Keyboard navigation handler
  const handleSearchKeyDown = (e) => {
    if (!results.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIdx(prev =>
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIdx(prev => prev > 0 ? prev - 1 : -1);
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIdx >= 0) {
          handleResultClick(results[highlightedIdx]);
          setHighlightedIdx(-1);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setHighlightedIdx(-1);
        setResults([]);
        break;
    }
  };

  const handleResultClick = (result) => {
    const resultKey = `${result.termId}:${result.lang}`;
    const currentKey = selectedResult ? `${selectedResult.termId}:${selectedResult.lang}` : '';
    setSelectedResult(currentKey === resultKey ? null : result);
    setDetailStack([]);
  };

  const openDetailPanel = async (term, lang) => {
    const normalizedLang = (lang || '').toLowerCase().trim();
    const normalizedTerm = String(term || '').trim();
    if (!normalizedTerm) {
      return;
    }

    const lookupKey = `${normalizedTerm.toLowerCase()}|${normalizedLang}`;
    const cachedPanel = lookupCacheRef.current.get(lookupKey);
    if (cachedPanel) {
      setDetailStack((prev) => [...prev, cachedPanel]);
      return;
    }

    try {
      const response = await axios.get('/api/lookup', {
        params: {
          term: normalizedTerm,
          lang,
        },
      });

      const candidates = response.data.results || [];
      const found =
        candidates.find(
          (r) =>
            r.term?.toLowerCase() === normalizedTerm.toLowerCase() &&
            (!normalizedLang || !r.lang || r.lang.toLowerCase() === normalizedLang)
        ) ||
        candidates.find((r) => !normalizedLang || !r.lang || r.lang.toLowerCase() === normalizedLang) ||
        candidates[0];

      if (found) {
        const panel = {
          ...found,
          lang: found.lang || normalizedLang || '',
        };
        lookupCacheRef.current.set(lookupKey, panel);
        setDetailStack((prev) => [...prev, panel]);
      }
    } catch {
      // ignore lookup misses; keep current stack
    }
  };

  const closeDetailPanel = (index) => {
    setDetailStack(detailStack.filter((_, i) => i !== index));
  };

  const closeAllDetailPanels = () => {
    setDetailStack([]);
  };

  return (
    <div className="app">
      <div className="header">
        <div className="header-top">
          <div>
            <h1>📚 Etymology Search</h1>
            <p>Search through 4M+ etymology relationships</p>
          </div>
          <div className="backend-native-label">Running native Cloudflare search</div>
        </div>
      </div>

      {latencies.length > 0 && (
        <div className="latency-ticker">
          Latency (network/total): {latencies.map(l => `${l}ms`).join(' • ')}
        </div>
      )}

      <div className="search-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search for a word (e.g., 'etymology', 'language', 'root')..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="search-input"
            autoFocus
          />
          {loading && <div className="spinner"></div>}
        </div>

        {error && <div className="error-message">❌ {error}</div>}

        <div className="results-container">
          <div className="results-list" ref={resultsListRef}>
            {results.length > 0 && (
              <div className="result-count">
                Found {results.length} result{results.length !== 1 ? 's' : ''}
              </div>
            )}

            {results.slice(0, 50).map((result, idx) => (
              <div
                key={`${result.termId}-${idx}`}
                className={`result-item ${
                  highlightedIdx === idx ? 'highlighted' : ''
                } ${selectedResult?.termId === result.termId && selectedResult?.lang === result.lang ? 'selected' : ''}`}
                onClick={() => handleResultClick(result)}
                onMouseEnter={() => setHighlightedIdx(idx)}
                onMouseLeave={() => setHighlightedIdx(-1)}
              >
                <div className="result-header">
                  <span className="result-term">{result.term}</span>
                  <span className="result-lang">{result.lang}</span>
                </div>
                {result.related.length > 0 && (
                  <div className="result-related">
                    Related: {result.related.slice(0, 3).join(', ')}
                    {result.related.length > 3 && ` +${result.related.length - 3}`}
                  </div>
                )}
              </div>
            ))}

            {query && results.length === 0 && !loading && (
              <div className="no-results">
                No results found for "{query}"
              </div>
            )}
          </div>

          {selectedResult && (
            <div className="detail-panel">
              <div className="detail-header">
                <h2>{selectedResult.term}</h2>
                <span className="detail-lang">{selectedResult.lang}</span>
                <button
                  className="close-btn"
                  onClick={() => setSelectedResult(null)}
                >
                  ✕
                </button>
              </div>

              <div className="detail-content">
                <div className="detail-section">
                  <h3>Languages</h3>
                  <div className="languages">
                    {selectedResult.languages.map((lang) => (
                      <span key={lang} className="language-badge">
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>

                {selectedResult.relations.length > 0 && (
                  <div className="detail-section">
                    <h3>Etymological Relations</h3>
                    <div className="relations">
                      {selectedResult.relations.slice(0, 20).map((rel, idx) => (
                        <div key={idx} className="relation">
                          <span className="rel-type">{rel.type}</span>
                          <button
                            className="rel-term-btn"
                            onClick={() => openDetailPanel(rel.term, rel.lang)}
                          >
                            {rel.term}
                          </button>
                          <span className="rel-lang">{rel.lang}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedResult.related.length > 0 && (
                  <div className="detail-section">
                    <h3>Related Terms ({selectedResult.related.length})</h3>
                    <div className="related-terms">
                      {selectedResult.related.map((term, idx) => (
                        <button
                          key={idx}
                          className="related-badge-btn"
                          onClick={() => openDetailPanel(term, selectedResult.lang)}
                        >
                          {term}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {detailStack.length > 0 && (
            <div className="stacked-panels">
              {detailStack.map((panel, stackIdx) => (
                <div key={`${panel.termId}-${stackIdx}`} className="stacked-panel">
                  <div className="detail-header">
                    <h2>{panel.term}</h2>
                    <span className="detail-lang">{panel.lang}</span>
                    <button
                      className="close-btn"
                      onClick={() => closeDetailPanel(stackIdx)}
                    >
                      ✕
                    </button>
                  </div>

                  <div className="detail-content">
                    <div className="detail-section">
                      <h3>Languages</h3>
                      <div className="languages">
                        {panel.languages.map((lang) => (
                          <span key={lang} className="language-badge">
                            {lang}
                          </span>
                        ))}
                      </div>
                    </div>

                    {panel.relations.length > 0 && (
                      <div className="detail-section">
                        <h3>Etymological Relations</h3>
                        <div className="relations">
                          {panel.relations.slice(0, 20).map((rel, idx) => (
                            <div key={idx} className="relation">
                              <span className="rel-type">{rel.type}</span>
                              <button
                                className="rel-term-btn"
                                onClick={() => openDetailPanel(rel.term, rel.lang)}
                              >
                                {rel.term}
                              </button>
                              <span className="rel-lang">{rel.lang}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {panel.related.length > 0 && (
                      <div className="detail-section">
                        <h3>Related Terms ({panel.related.length})</h3>
                        <div className="related-terms">
                          {panel.related.map((term, idx) => (
                            <button
                              key={idx}
                              className="related-badge-btn"
                              onClick={() => openDetailPanel(term, panel.lang)}
                            >
                              {term}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
